import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import Logo from '../../assets/IBM-Q2D-Logo.png';

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        fontFamily: 'Helvetica'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: '#ccc',
        borderBottomStyle: 'solid',
        paddingBottom: 10,
        marginBottom: 10
    },
    logoContainer: {
        width: '60%',
        flexDirection: 'row',
        alignItems: 'center'
    },
    logo: {
        width: 60,
        height: 60,
        marginRight: 10
    },
    orgInfo: {
        flexDirection: 'column'
    },
    orgName: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#000'
    },
    orgText: {
        fontSize: 9,
        color: '#444',
        textAlign: 'center'
    },
    orgLink: {
        color: '#0066cc',
        textDecoration: 'none'
    },
    gstInfo: {
        fontSize: 8,
        color: '#555',
        marginTop: 2
    },
    rightHeader: {
        width: '40%',
        alignItems: 'flex-end',
        justifyContent: 'center'
    },
    receiptTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
        marginVertical: 12,
        color: '#2c3e50',
        textTransform: 'uppercase'
    },
    metadataSection: {
        marginBottom: 15
    },
    detailRow: {
        flexDirection: 'row',
        marginBottom: 5
    },
    label: {
        width: '40%',
        fontWeight: 'bold',
        color: '#444'
    },
    value: {
        width: '60%',
        color: '#000'
    },
    table: {
        marginTop: 10,
        borderWidth: 1,
        borderColor: '#999'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#999'
    },
    tableHeader: {
        fontWeight: 'bold',
        color: '#000',
        textAlign: 'center',
        padding: 5,
        borderRightWidth: 1,
        borderRightColor: '#999'
    },
    tableCell: {
        width: '10%',
        color: '#222',
        textAlign: 'center',
        padding: 5,
        borderRightWidth: 1,
        borderRightColor: '#999'
    },
    descriptionCell: {
        width: '70%',
        color: '#222',
        padding: 5,
        borderRightWidth: 1,
        borderRightColor: '#999'
    },
    amountCell: {
        width: '20%',
        color: '#222',
        textAlign: 'right',
        padding: 5,
        borderRightWidth: 1,
        borderRightColor: '#999',
        fontStyle: 'normal'
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingTop: 8,
        paddingRight: 5
    },
    summary: {
        marginTop: 10,
        fontSize: 10,
        color: '#000'
    },
    amountInWords: {
        marginTop: 5,
        padding: 5,
        borderWidth: 1,
        borderColor: '#999',
        fontStyle: 'italic'
    },
    note: {
        fontSize: 8,
        color: '#777',
        textAlign: 'center',
        marginTop: 25,
        fontStyle: 'italic'
    },
    footerAddress: {
        fontSize: 8,
        color: '#555',
        textAlign: 'center',
        marginTop: 5
    },
    footerLinks: {
        fontSize: 9,
        color: '#444',
        textAlign: 'center',
        marginTop: 5
    },
    signatureSection: {
        marginTop: 30,
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },
    signatureLine: {
        width: 200,
        borderTopWidth: 1,
        borderTopColor: '#000',
        marginTop: 40,
        textAlign: 'center',
        paddingTop: 3
    }
});

const ReceiptPDF = ({ application, payment }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const convertToWords = (num) => {
        const single = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const double = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const twoDigits = (n) => {
            if (n < 10) return single[n];
            if (n < 20) return double[n - 10];
            return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + single[n % 10] : '');
        };

        const threeDigits = (n) => {
            let str = '';
            if (Math.floor(n / 100) > 0) {
                str += single[Math.floor(n / 100)] + ' Hundred ';
            }
            const rem = n % 100;
            if (rem > 0) str += twoDigits(rem);
            return str.trim();
        };

        if (num === 0) return 'Zero Rupees Only';

        let result = '';
        if (Math.floor(num / 100000) > 0) {
            result += twoDigits(Math.floor(num / 100000)) + ' Lakh ';
            num %= 100000;
        }
        if (Math.floor(num / 1000) > 0) {
            result += twoDigits(Math.floor(num / 1000)) + ' Thousand ';
            num %= 1000;
        }

        if (num > 0) {
            result += threeDigits(num);
        }

        return result.trim() + ' Rupees Only';
    };

    const isKeralaApplicant = application.state && application.state.toUpperCase() === 'KERALA';
    const totalAmount = application.amount || 2000;
    const taxableAmount = Math.round(totalAmount / (1 + (isKeralaApplicant ? 0.18 : 0.18)) * 100) / 100;
    const gstAmount = Math.round((totalAmount - taxableAmount) * 100) / 100;

    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    if (isKeralaApplicant) {
        cgstAmount = Math.round(gstAmount / 2 * 100) / 100;
        sgstAmount = gstAmount - cgstAmount;
    } else {
        igstAmount = gstAmount;
    }

    const amountInWords = convertToWords(totalAmount);

    const fullQualification = application.educationQualification || '';
    const [course, ...specializationParts] = fullQualification.split(',');
    const specialization = specializationParts.join(',').trim();

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        <Image style={styles.logo} src={Logo} />
                        <View style={styles.orgInfo}>
                            <Text style={styles.orgText}>Sattva Global City, Mysore Road,</Text>
                            <Text style={styles.orgText}>Rajarajeshwari Nagar, Bengaluru - 560059</Text>
                            <Text style={styles.gstInfo}>GSTN: 32AABCQ1286G1ZO</Text>
                        </View>
                    </View>
                    <View style={styles.rightHeader}>
                        <Text style={styles.label}>Date: {formatDate(application.createdAt)}</Text>
                    </View>
                </View>

                {/* Title */}
                <Text style={styles.receiptTitle}>Application Fee Receipt</Text>

                {/* Metadata */}
                <View style={styles.metadataSection}>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Receipt Number:</Text>
                        <Text style={styles.value}>{application.applicationNo || '-'}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Name of Candidate:</Text>
                        <Text style={styles.value}>{application.name || '-'}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Course:</Text>
                        <Text style={styles.value}>{course.trim() || '-'}</Text>
                    </View>
                    {specialization && (
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Specialization:</Text>
                            <Text style={styles.value}>{specialization}</Text>
                        </View>
                    )}
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Date of Receipt:</Text>
                        <Text style={styles.value}>{formatDate(application.createdAt)}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Payment Date:</Text>
                        <Text style={styles.value}>{formatDate(payment?.paymentTime)}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Payment Mode:</Text>
                        <Text style={styles.value}>Online Payment</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.label}>Transaction ID:</Text>
                        <Text style={styles.value}>{application.orderId || '-'}</Text>
                    </View>
                </View>

                {/* Table */}
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>Sl No</Text>
                        <Text style={[styles.descriptionCell, { fontWeight: 'bold' }]}>Description [999295]</Text>
                        <Text style={[styles.amountCell, { fontWeight: 'bold' }]}>Amount</Text>
                    </View>

                    {/* Application Fee Row */}
                    <View style={styles.tableRow}>
                        <Text style={styles.tableCell}>1</Text>
                        <Text style={styles.descriptionCell}>
                            Application fee for {application.educationQualification || 'Course'}
                        </Text>
                        <Text style={styles.amountCell}>{taxableAmount.toFixed(2)}</Text>
                    </View>

                    {/* GST Rows */}
                    {isKeralaApplicant ? (
                        <>
                            <View style={styles.tableRow}>
                                <Text style={styles.tableCell}></Text>
                                <Text style={[styles.descriptionCell, { fontStyle: 'italic', fontSize: 8 }]}>+ CGST @ 9%</Text>
                                <Text style={styles.amountCell}>{cgstAmount.toFixed(2)}</Text>
                            </View>
                            <View style={styles.tableRow}>
                                <Text style={styles.tableCell}></Text>
                                <Text style={[styles.descriptionCell, { fontStyle: 'italic', fontSize: 8 }]}>+ SGST @ 9%</Text>
                                <Text style={styles.amountCell}>{sgstAmount.toFixed(2)}</Text>
                            </View>
                        </>
                    ) : (
                        <View style={styles.tableRow}>
                            <Text style={styles.tableCell}></Text>
                            <Text style={[styles.descriptionCell, { fontStyle: 'italic', fontSize: 8 }]}>+ IGST @ 18%</Text>
                            <Text style={styles.amountCell}>{igstAmount.toFixed(2)}</Text>
                        </View>
                    )}

                    {/* Total Row */}
                    <View style={[styles.tableRow, { backgroundColor: '#f5f5f5' }]}>
                        <Text style={[styles.tableCell, { fontWeight: 'bold' }]}></Text>
                        <Text style={[styles.descriptionCell, { fontWeight: 'bold' }]}>Total</Text>
                        <Text style={[styles.amountCell, { fontWeight: 'bold' }]}>{totalAmount.toFixed(2)}</Text>
                    </View>
                </View>


                {/* Summary */}
                <View style={styles.summary}>
                    <Text style={styles.amountInWords}>Amount in Words: {amountInWords}</Text>
                </View>

                {/* Footer */}
                <View style={{ marginTop: 20 }}>
                    <Text style={styles.note}>
                        This is a computer generated receipt and does not require a physical signature.
                    </Text>

                    <Text style={styles.footerLinks}>
                        Website: www.q2d.in | Email: info@q2d.in
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export default ReceiptPDF;